import { getNursePatients } from "../../features/schedule/derivedAssignments"
import {
	startTimeAllCases,
	endTimeAllCases,
	confirmedCancellationNoShow,
	confirmedCompletedAllCases,
	rangesOverlap,
	startOneWithinOther
} from "../../utils/realTimeUtils"
import reassignPatients from "./reassignPatients"

/**
 * Computes patient reassignments when a nurse's shift is shortened or the
 * nurse is removed mid-day.
 *
 * Affected patients = patients of the removed nurse whose appointment lies
 * within the affected window (interpreted via `beginning`).
 *
 * @param {Object} nurseToRemove
 * @param {{patientSchedule, nurseSchedule, chairSchedule}} schedule
 * @param {boolean} beginning - true if trimming the start of the shift, false
 *   if trimming the end
 * @param {number} startTime - start of the affected window (minutes)
 * @param {number} endTime - end of the affected window (minutes)
 * @param {Object} settings - must include maxAcuityForNurse, maxPatientsForNurse,
 *   oneToOneAcuityLevel
 * @param {boolean} sandbox
 * @param {boolean} isTomorrowMode
 * @returns {Array<{patientId, newNurseId}>}
 */
const getAssignmentsForNurseRemoval = (
	nurseToRemove,
	schedule,
	beginning,
	startTime,
	endTime,
	settings,
	sandbox,
	isTomorrowMode
) => {
	const affectedPatients = getNursePatients(
		schedule.patientSchedule,
		nurseToRemove.nurseId
	).filter(patient => {
		if (confirmedCancellationNoShow(patient)) return false
		if (confirmedCompletedAllCases(patient, sandbox, isTomorrowMode)) return false

		const range = [
			startTimeAllCases(patient, sandbox, isTomorrowMode),
			endTimeAllCases(patient, sandbox, isTomorrowMode)
		]
		return beginning
			? startOneWithinOther(range, [startTime, endTime])
			: rangesOverlap([startTime, endTime], range)
	})

	return reassignPatients({
		patientsToMove: affectedPatients,
		candidateNurses: schedule.nurseSchedule.filter(n => n.nurseId !== nurseToRemove.nurseId),
		schedule,
		settings,
		sandbox,
		isTomorrowMode,
		allowBumping: true
	})
}

export default getAssignmentsForNurseRemoval
